/**
 * Config fingerprint — the "one-lever guarantee" for the benchmark study
 * (DEV-17198).
 *
 * This epic's entire method is "two runs identical except one thing." That
 * discipline is only trustworthy if we can capture the COMPLETE configuration
 * of a trial as a structured, content-addressed value and then diff any two
 * such values axis-by-axis — WITHIN one benchmark or ACROSS different
 * benchmarks — to see, exhaustively, exactly which levers differ.
 *
 * A fingerprint is benchmark-AGNOSTIC: it identifies a config, not a run, so
 * two runs from different benchmark definitions are directly diffable. The
 * hash is an IDENTITY hash (are these two configs the same), reusing the same
 * pure-JS {@link cyrb53} the memory-fixture corpus uses so `@cortex/types`
 * stays isomorphic/browser-safe — never security.
 *
 * The axes mirror the ticket's "confounding axes":
 *  - model       — modelConfigId + provider + provider model id
 *  - prompt      — instructions override (hashed) + baseline prompt hash
 *  - tools       — effective toolRefs (order-independent) + fromProfile + disabled
 *  - codeMode    — on/off; `null` = not yet a clean arm lever (DEV-17197)
 *  - memory      — mode (OFF/LIVE/SEEDED) + pinned-fixture hash
 *  - environment — scenario id + version + sim commit + world + group + stack
 *  - runtime     — host build + watchdog params (best-effort; may be absent)
 *
 * `model`/`prompt`/`tools`/`codeMode`/`memory` are the LEVERS an experiment
 * arm varies. `environment` is the SCORING BASIS: outcome numbers are only
 * commensurable when (scenario, scenarioVersion, simCommit) match — see
 * {@link compareConfigFingerprints}.
 */
import { z } from 'zod';
/**
 * cyrb53 — a fast, well-distributed non-cryptographic 53-bit hash. Pure JS (no
 * `node:crypto`) so this module stays browser-safe. Identity, never security.
 * Kept local (not exported from memory-fixture) so the two hashers can't drift.
 */
function cyrb53(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const n = 4294967296 * (2097151 & h2) + (h1 >>> 0);
    return n.toString(16).padStart(14, '0');
}
/**
 * Deterministic JSON: objects serialized with keys sorted recursively, so two
 * structurally-equal values always produce the same string regardless of key
 * insertion order. Arrays keep their order (order is meaningful for e.g. a
 * baseline list); callers that want order-independence sort before hashing.
 */
function canonicalJson(value) {
    return JSON.stringify(sortDeep(value));
}
function sortDeep(value) {
    if (Array.isArray(value))
        return value.map(sortDeep);
    if (value && typeof value === 'object') {
        const out = {};
        for (const k of Object.keys(value).sort()) {
            out[k] = sortDeep(value[k]);
        }
        return out;
    }
    return value;
}
/** A stable per-axis sub-hash, so a diff can compare axes without deep-equal. */
function axisHash(value) {
    return cyrb53(canonicalJson(value));
}
/**
 * Content-address an arbitrary config string (a prompt/charter) as `p_<cyrb53>`.
 * Exported so the run-capture path hashes a definition's prompt with the SAME
 * function the fingerprint uses — the two can never drift.
 */
export function hashConfigText(text) {
    return `p_${cyrb53(text)}`;
}
// ─────────────────────────────────────────────────────────────────────────────
// Axis schemas
// ─────────────────────────────────────────────────────────────────────────────
export const ModelAxisSchema = z
    .object({
    modelConfigId: z.string().nullable(),
    provider: z.string().nullable(),
    /** Provider model id (e.g. claude-sonnet-5). */
    modelId: z.string().nullable(),
})
    .strict();
export const PromptAxisSchema = z
    .object({
    /** cyrb53 of the arm's `instructions` override, or null when unset. */
    overrideHash: z.string().nullable(),
    /** cyrb53 of the baseline definition prompt(s), or null when uncaptured. */
    baselineHash: z.string().nullable(),
})
    .strict();
export const ToolsAxisSchema = z
    .object({
    /** Effective toolRefs, order-normalized (sorted) — refs are a set. */
    refs: z.array(z.string()),
    /** True when the arm profile replaced the definition's toolRefs. */
    fromProfile: z.boolean(),
    /** Sorted disabledTools ids; [] when the arm withheld nothing. */
    disabled: z.array(z.string()),
})
    .strict();
export const MemoryAxisSchema = z
    .object({
    /** null = not captured; otherwise the resolved arm memory mode. */
    mode: z.enum(['OFF', 'LIVE', 'SEEDED']).nullable(),
    /** SEEDED corpus hash (fixtureHash), else null. */
    fixtureHash: z.string().nullable(),
    /**
     * The save-guidance write policy the arm ran (DEV-17246, §4.9): the
     * versioned default block's version id (`sg1`, …) or the `p_<hash>` of a
     * per-arm `profile.saveGuidance` override. null = not captured (legacy
     * runs that predate guidance capture). Guidance is a creation-pipeline
     * lever, so two arms differing only here must not fingerprint equal.
     */
    guidanceVersion: z.string().nullable(),
    /**
     * Combined `p_<hash>` over the launched definitions' custom principle-judge
     * prompts (`config.reflection.judgePrompt`, PR #1123) — the judge prompt is
     * a versioned, fingerprinted prompt asset exactly like save guidance
     * (A.1.32 rule 5). null = every definition ran the built-in default (or
     * legacy uncaptured).
     */
    judgePromptHash: z.string().nullable(),
    /**
     * The memory namespace the arm wrote into (DEV-17254, §6.5): null = the
     * arm carried no namespace (OFF/SEEDED, or a legacy uncaptured run). Two
     * LIVE arms in different namespaces are different memory configurations
     * by construction — the namespace is the corpus identity, which is what
     * lets confound detection see accidental cross-run carryover.
     */
    namespaceId: z.string().nullable(),
    /**
     * The morning-after capture lever (DEV-17324): false = the arm ablated
     * post-trial capture (C-prime), null = capture at its default (on for
     * LIVE) or uncaptured. Normalized at compute time so an EXPLICIT
     * trialCapture: true fingerprints identically to the default — only the
     * ablation is a lever. Two LIVE arms differing only here must not
     * fingerprint equal (C vs C-prime is the one-lever capture comparison).
     */
    trialCapture: z.boolean().nullable(),
})
    .strict();
export const EnvironmentAxisSchema = z
    .object({
    /** Scenario key (scoring basis). */
    scenario: z.string().nullable(),
    scenarioVersion: z.number().int().nullable(),
    /** Sim git commit the scorecard was produced against (scoring basis). */
    simCommit: z.string().nullable(),
    world: z.string().nullable(),
    orchestrationGroupId: z.string().nullable(),
    /** Which agent stack ran (metaphysics-orchestration / classical-*). */
    stack: z.string().nullable(),
})
    .strict();
export const RuntimeAxisSchema = z
    .object({
    /** Host build/version, when the run recorded it. */
    hostBuild: z.string().nullable(),
    /** Watchdog per-call timeout (seconds), when recorded. */
    callTimeoutSec: z.number().nullable(),
})
    .strict();
/** The lever axes an experiment arm is allowed to vary (confound counting). */
export const leverAxes = ['model', 'prompt', 'tools', 'codeMode', 'memory'];
/** The scoring-basis axis — differences here break outcome commensurability. */
export const environmentAxis = 'environment';
export const configAxes = [...leverAxes, environmentAxis, 'runtime'];
export const ConfigFingerprintSchema = z
    .object({
    /** Fingerprint schema version — bump when the axis set changes. */
    version: z.literal('cfp2'),
    /** Content-addressed identity over all axes (cyrb53, `cfp2_`-prefixed). */
    hash: z.string(),
    model: ModelAxisSchema,
    prompt: PromptAxisSchema,
    tools: ToolsAxisSchema,
    /** null = code-mode isn't captured as a clean lever yet (DEV-17197). */
    codeMode: z.boolean().nullable(),
    memory: MemoryAxisSchema,
    environment: EnvironmentAxisSchema,
    runtime: RuntimeAxisSchema,
})
    .strict();
/** The raw axis values a caller extracts from a run + its benchmark context. */
export const ConfigFingerprintInputSchema = z
    .object({
    model: ModelAxisSchema.partial().optional(),
    /** Raw instructions override (hashed here) — null/undefined = none. */
    instructionsOverride: z.string().nullable().optional(),
    /** Raw baseline prompt(s) (hashed here) — null/undefined = uncaptured. */
    baselinePrompt: z.string().nullable().optional(),
    /**
     * Pre-hashed baseline prompt (already `p_<hash>`), used directly as the
     * prompt axis's baselineHash. Takes precedence over `baselinePrompt` — the
     * run-capture path stores a hash (not the full 100k-char charter) per
     * definition, so the serializer passes the hash straight through.
     */
    baselinePromptHash: z.string().nullable().optional(),
    toolRefs: z.array(z.string()).optional(),
    toolsFromProfile: z.boolean().optional(),
    /**
     * `disabledTools` ids for the tools axis (DEV-17197). Unset / empty both
     * become `[]` so two arms that withheld nothing hash equal. Sorted at
     * compute time — order is not identity.
     */
    toolsDisabled: z.array(z.string()).optional(),
    codeMode: z.boolean().nullable().optional(),
    memoryMode: z.enum(['OFF', 'LIVE', 'SEEDED']).nullable().optional(),
    memoryFixtureHash: z.string().nullable().optional(),
    /**
     * Save-guidance identity for the memory axis (DEV-17246): the default
     * block's version id or a `p_<hash>` of the arm's override, captured on
     * the run's resolvedProfile at creation. null/undefined = uncaptured.
     */
    saveGuidanceVersion: z.string().nullable().optional(),
    /** Combined judge-prompt hash (pre-hashed `p_<…>`); null = defaults. */
    judgePromptHash: z.string().nullable().optional(),
    /** The arm's memory namespace id (DEV-17254); null/absent = none. */
    memoryNamespaceId: z.string().nullable().optional(),
    /** profile.trialCapture (DEV-17324); only false is a lever (see axis doc). */
    trialCapture: z.boolean().nullable().optional(),
    environment: EnvironmentAxisSchema.partial().optional(),
    runtime: RuntimeAxisSchema.partial().optional(),
})
    .strict();
// ─────────────────────────────────────────────────────────────────────────────
// Compute
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Build a {@link ConfigFingerprint} from extracted axis values. Pure and total:
 * every axis gets a fully-populated (null-filled) value so two fingerprints are
 * always axis-by-axis comparable, and the `hash` is stable regardless of input
 * key order or toolRefs order.
 */
export function computeConfigFingerprint(input) {
    const model = {
        modelConfigId: input.model?.modelConfigId ?? null,
        provider: input.model?.provider ?? null,
        modelId: input.model?.modelId ?? null,
    };
    const prompt = {
        overrideHash: input.instructionsOverride != null ? hashConfigText(input.instructionsOverride) : null,
        baselineHash: input.baselinePromptHash ??
            (input.baselinePrompt != null ? hashConfigText(input.baselinePrompt) : null),
    };
    const tools = {
        // Order-independent: refs are a set, so sort for a stable hash.
        refs: [...(input.toolRefs ?? [])].sort(),
        fromProfile: input.toolsFromProfile ?? false,
        // Same order-normalization as refs — disabledTools is a set. Participates
        // in the identity hash via `canonicalJson(axes)` below, so two arms that
        // differ only in disabled tools do not collide.
        disabled: [...(input.toolsDisabled ?? [])].sort(),
    };
    const memory = {
        mode: input.memoryMode ?? null,
        fixtureHash: input.memoryFixtureHash ?? null,
        guidanceVersion: input.saveGuidanceVersion ?? null,
        judgePromptHash: input.judgePromptHash ?? null,
        namespaceId: input.memoryNamespaceId ?? null,
        trialCapture: input.trialCapture === false ? false : null,
    };
    const environment = {
        scenario: input.environment?.scenario ?? null,
        scenarioVersion: input.environment?.scenarioVersion ?? null,
        simCommit: input.environment?.simCommit ?? null,
        world: input.environment?.world ?? null,
        orchestrationGroupId: input.environment?.orchestrationGroupId ?? null,
        stack: input.environment?.stack ?? null,
    };
    const runtime = {
        hostBuild: input.runtime?.hostBuild ?? null,
        callTimeoutSec: input.runtime?.callTimeoutSec ?? null,
    };
    const codeMode = input.codeMode ?? null;
    const axes = { model, prompt, tools, codeMode, memory, environment, runtime };
    return { version: 'cfp2', hash: `cfp2_${cyrb53(canonicalJson(axes))}`, ...axes };
}
// ─────────────────────────────────────────────────────────────────────────────
// Diff + confound + commensurability
// ─────────────────────────────────────────────────────────────────────────────
export const ConfigAxisDiffSchema = z.object({
    axis: z.enum(configAxes),
    changed: z.boolean(),
    /** Structured value of the axis on side A / side B (for the compare matrix). */
    a: z.unknown(),
    b: z.unknown(),
});
/** One axis's value on each fingerprint (used for the diff). */
function axisValue(fp, axis) {
    return fp[axis];
}
/**
 * Axis-by-axis diff of two fingerprints. Each entry says same-or-different plus
 * both values, so the frontend renders one column per axis with cells
 * highlighted where they differ. Works across benchmarks — a fingerprint is
 * benchmark-agnostic.
 */
export function diffConfigFingerprints(a, b) {
    return configAxes.map((axis) => {
        const av = axisValue(a, axis);
        const bv = axisValue(b, axis);
        return { axis, changed: axisHash(av) !== axisHash(bv), a: av, b: bv };
    });
}
export const ConfigComparisonSchema = z.object({
    /** Full axis-by-axis diff. */
    diff: z.array(ConfigAxisDiffSchema),
    /** Names of the LEVER axes that differ (model/prompt/tools/codeMode/memory). */
    changedLeverAxes: z.array(z.enum(leverAxes)),
    /** Names of ALL axes that differ, including environment/runtime. */
    changedAxes: z.array(z.enum(configAxes)),
    /**
     * True when the comparison was declared to isolate a single lever but more
     * than one lever axis actually moved — the comparison is confounded and the
     * headline delta can't be attributed to the intended lever.
     */
    confounded: z.boolean(),
    /** The lever the caller claimed to isolate (echoed for the badge), if any. */
    isolatedLever: z.enum(leverAxes).optional(),
    /**
     * Whether OUTCOME numbers are comparable: only when the scoring basis
     * (scenario id + version + sim commit) matches. Config diff is always valid;
     * outcome deltas must be suppressed/badged when this is false.
     */
    outcomeComparable: z.boolean(),
    /** Why outcomes aren't comparable (scoring-basis mismatch), when applicable. */
    incomparableReason: z.string().optional(),
});
const scoringBasisKeys = ['scenario', 'scenarioVersion', 'simCommit'];
/**
 * Compare two fingerprints: axis diff + confound detection + commensurability
 * guard. This is the enforcement point of the one-lever guarantee.
 */
export function compareConfigFingerprints(a, b, opts = {}) {
    const diff = diffConfigFingerprints(a, b);
    const changedAxes = diff.filter((d) => d.changed).map((d) => d.axis);
    const changedLeverAxes = changedAxes.filter((ax) => leverAxes.includes(ax));
    // Outcome commensurability: scoring basis must match, else deltas are unlike.
    const basisMismatch = scoringBasisKeys.filter((k) => a.environment[k] !== b.environment[k]);
    const outcomeComparable = basisMismatch.length === 0;
    // Confound: within-arm trials must match on EVERY axis; a lever-isolation
    // comparison must move at most the one named lever.
    const confounded = opts.sameArmTrials
        ? changedAxes.length > 0
        : opts.isolatesLever
            ? changedLeverAxes.filter((ax) => ax !== opts.isolatesLever).length > 0
            : false;
    return {
        diff,
        changedLeverAxes,
        changedAxes,
        confounded,
        ...(opts.isolatesLever ? { isolatedLever: opts.isolatesLever } : {}),
        outcomeComparable,
        ...(outcomeComparable
            ? {}
            : {
                incomparableReason: `outcome numbers are not comparable — scoring basis differs on ` +
                    `${basisMismatch.join(', ')} (same scenario id + version + sim commit required).`,
            }),
    };
}
//# sourceMappingURL=config-fingerprint.js.map